import { BaseDesignPersistence, CustomizerEngine, SerializedLayer } from 'smg/customizer';
import { KeepUploads } from 'smg/uploader/v2';
import { PinImageState, PinImageUploadUI } from './image-upload-ui';
import { PinTextDropUI } from './text-drop-ui';

export interface PinDesignState {
    /** Shape type + size row id — restore re-selects these before rebuilding layers */
    shapeType: string;
    sizeID: string;
    /** Canvas px the state was captured on — layer restores rescale against this */
    canvas: { width: number; height: number };
    bgColor: string;
    imageStates: PinImageState[];
    textDropStates: SerializedLayer[];
    savedAt: number;
}

export interface PinDesignPersistenceConfig {
    /** Current shape/size/background selection (the page owns those controls) */
    currentSelection: () => Omit<PinDesignState, 'imageStates' | 'textDropStates' | 'savedAt'> | null;
    /** Re-select the saved shape/size/background and render — runs AFTER the layer
     * maps are seeded, so the resulting attachTo() rebuilds everything */
    applySelection: (state: PinDesignState) => Promise<void>;
}

/**
 * Design persistence for the pin builder. Unlike coins' two persistent engines, pins
 * recreates its engine per shape/size switch and ALL its content is dynamic layers —
 * so the saved state is just the selection (shape/size/bg) plus the upload and text
 * snapshots, no engine serialization. The page re-hooks autosave on every rebuild via
 * hookEngine().
 */
export class PinDesignPersistence extends BaseDesignPersistence<PinDesignState> {
    #imageUI: PinImageUploadUI;
    #textDropUI: PinTextDropUI;
    #config: PinDesignPersistenceConfig;

    constructor(imageUI: PinImageUploadUI, textDropUI: PinTextDropUI, config: PinDesignPersistenceConfig) {
        super('pin-design-state');
        this.#imageUI = imageUI;
        this.#textDropUI = textDropUI;
        this.#config = config;
    }

    /** Autosave on this engine's changes — call after every engine recreation */
    hookEngine(engine: CustomizerEngine): void {
        engine.on('significantChange', () => void this.save());
    }

    override async getCurrentState(): Promise<PinDesignState | null> {
        const selection = this.#config.currentSelection();
        if (!selection) {
            return null;
        }

        // Blob URLs die on reload — trade upload IDs for permanent CDN URLs first
        const uploadIDs = this.#imageUI.getUploadIDs();
        if (uploadIDs.length > 0) {
            const permanentUrls = await KeepUploads(uploadIDs);
            if (permanentUrls.size > 0) {
                this.#imageUI.setPermanentUrls(permanentUrls);
            }
        }

        return {
            ...selection,
            imageStates: this.#imageUI.getState(),
            textDropStates: this.#textDropUI.getState(),
            savedAt: Date.now(),
        };
    }

    protected override hasContent(state: PinDesignState): boolean {
        if (state.imageStates.length > 0 || state.bgColor) {
            return true;
        }
        return state.textDropStates.some(l => Boolean(l.text && l.text.trim().length > 0));
    }

    protected override async applyState(state: PinDesignState): Promise<boolean> {
        // Seed the layer maps first — applySelection's render then rebuilds them
        // through the same attachTo path as any shape/size switch
        this.#imageUI.restoreState(state.imageStates);
        await this.#textDropUI.restoreState(state.textDropStates, state.canvas);
        await this.#config.applySelection(state);
        return true;
    }
}
